interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'light' | 'dark';
  className?: string;
}

export function Logo({ 
  size = 'md',
  variant = 'default',
  className = ''
}: LogoProps) {
  const sizeClasses = {
    sm: 'h-16',
    md: 'h-20',
    lg: 'h-28',
    xl: 'h-36'
  };

  // Color schemes for different variants
  const colors = {
    default: {
      primary: '#2d5016',    // Forest green
      secondary: '#c41e3a',  // Deep red
      accent: '#f4a460',     // Gold
      text: '#2d3319'        // Dark foreground
    },
    light: {
      primary: '#5a7139',    // Lighter green
      secondary: '#e8624f',  // Lighter red
      accent: '#f4a460',     // Gold
      text: '#ffffff'        // White
    },
    dark: {
      primary: '#2d5016',    // Forest green
      secondary: '#c41e3a',  // Deep red
      accent: '#f4a460',     // Gold
      text: '#faf7f2'        // Cream
    }
  };

  const c = colors[variant];

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 400 120"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Better to Give than Receive - Give Better Gifts"
      >
        {/* Decorative snowflake/gift icon */}
        <g transform="translate(35, 60)">
          {/* Outer petals - Forest Green */}
          <path
            d="M 0,-30 L 3,-10 L 0,-8 L -3,-10 Z"
            fill={c.primary}
          />
          <path
            d="M 0,30 L -3,10 L 0,8 L 3,10 Z"
            fill={c.primary}
          />
          <path
            d="M -30,0 L -10,-3 L -8,0 L -10,3 Z"
            fill={c.primary}
          />
          <path
            d="M 30,0 L 10,3 L 8,0 L 10,-3 Z"
            fill={c.primary}
          />
          
          {/* Diagonal petals - Deep Red */}
          <path
            d="M -21,-21 L -7,-7 L -5,-9 L -9,-13 Z"
            fill={c.secondary}
          />
          <path
            d="M 21,21 L 7,7 L 9,5 L 13,9 Z"
            fill={c.secondary}
          />
          <path
            d="M -21,21 L -7,7 L -9,5 L -13,9 Z"
            fill={c.secondary}
          />
          <path
            d="M 21,-21 L 7,-7 L 5,-9 L 9,-13 Z"
            fill={c.secondary}
          />
          
          {/* Center circle - Gold */}
          <circle cx="0" cy="0" r="8" fill={c.accent} />
          
          {/* Inner details - alternating colors */}
          <circle cx="0" cy="0" r="5" fill={c.primary} opacity="0.8" />
          <circle cx="0" cy="0" r="3" fill={c.secondary} opacity="0.6" />
        </g>

        {/* Ribbon/bow accent */}
        <g transform="translate(35, 60)">
          <path
            d="M -12,-2 Q -15,-5 -12,-8 L -8,-6 L -12,-2 Z"
            fill={c.secondary}
            opacity="0.7"
          />
          <path
            d="M 12,-2 Q 15,-5 12,-8 L 8,-6 L 12,-2 Z"
            fill={c.secondary}
            opacity="0.7"
          />
        </g>

        {/* Main text: "BETTER TO GIVE THAN RECEIVE" */}
        <text
          x="79"
          y="50"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="18"
          fontWeight="700"
          letterSpacing="1.5"
          fill={c.secondary}
        >
          BETTER
        </text>
        <text
          x="163"
          y="50"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="11"
          fontWeight="600"
          letterSpacing="0.5"
          fill={c.primary}
          opacity="0.6"
        >
          TO
        </text>
        <text
          x="186"
          y="50"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="18"
          fontWeight="700"
          letterSpacing="1.5"
          fill={c.accent}
        >
          GIVE
        </text>
        <text
          x="238"
          y="50"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="11"
          fontWeight="600"
          letterSpacing="0.5"
          fill={c.primary}
          opacity="0.6"
        >
          THAN
        </text>
        <text
          x="278"
          y="50"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="18"
          fontWeight="700"
          letterSpacing="1.5"
          fill={c.primary}
        >
          RECEIVE
        </text>


        {/* Tagline: "GIVE BETTER GIFTS" - centered under main tagline */}
        <text
          x="115"
          y="75"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="14"
          fontWeight="600"
          letterSpacing="3"
          fill={c.accent}
        >
          GIVE
        </text>

        {/* Star between GIVE and BETTER */}
        <g transform="translate(168, 70)">
          <polygon
            points="0,-4 1.2,-1.2 4,-1.2 1.6,0.8 2.4,4 0,2 -2.4,4 -1.6,0.8 -4,-1.2 -1.2,-1.2"
            fill="white"
            stroke={c.primary}
            strokeWidth="1"
          />
        </g>

        <text
          x="178"
          y="75"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="14"
          fontWeight="600"
          letterSpacing="3"
          fill={c.secondary}
        >
          BETTER
        </text>

        {/* Star between BETTER and GIFTS */}
        <g transform="translate(257, 70)">
          <polygon
            points="0,-4 1.2,-1.2 4,-1.2 1.6,0.8 2.4,4 0,2 -2.4,4 -1.6,0.8 -4,-1.2 -1.2,-1.2"
            fill="white"
            stroke={c.primary}
            strokeWidth="1"
          />
        </g>

        <text
          x="265"
          y="75"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="14"
          fontWeight="600"
          letterSpacing="3"
          fill={c.primary}
        >
          GIFTS
        </text>
      </svg>
    </div>
  );
}

// Icon-only version for navigation/headers
export function LogoIcon({ 
  size = 'md', 
  variant = 'default',
  className = '' 
}: { 
  size?: 'sm' | 'md' | 'lg'; 
  variant?: 'default' | 'light' | 'dark';
  className?: string;
}) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16'
  };

  const colors = {
    default: {
      primary: '#2d5016',
      secondary: '#c41e3a',
      accent: '#f4a460'
    },
    light: {
      primary: '#5a7139',
      secondary: '#e8624f',
      accent: '#f4a460'
    },
    dark: {
      primary: '#2d5016',
      secondary: '#c41e3a',
      accent: '#f4a460'
    }
  };

  const c = colors[variant];

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 60 60"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="BtGtR"
      >
        {/* Outer petals - Forest Green */}
        <path
          d="M 30,5 L 33,25 L 30,27 L 27,25 Z"
          fill={c.primary}
        />
        <path
          d="M 30,55 L 27,35 L 30,33 L 33,35 Z"
          fill={c.primary}
        />
        <path
          d="M 5,30 L 25,27 L 27,30 L 25,33 Z"
          fill={c.primary}
        />
        <path
          d="M 55,30 L 35,33 L 33,30 L 35,27 Z"
          fill={c.primary}
        />
        
        {/* Diagonal petals - Deep Red */}
        <path
          d="M 12,12 L 23,23 L 21,25 L 18,22 Z"
          fill={c.secondary}
        />
        <path
          d="M 48,48 L 37,37 L 39,35 L 42,38 Z"
          fill={c.secondary}
        />
        <path
          d="M 12,48 L 23,37 L 21,35 L 18,38 Z"
          fill={c.secondary}
        />
        <path
          d="M 48,12 L 37,23 L 35,21 L 38,18 Z"
          fill={c.secondary}
        />
        
        {/* Center circles - Gold */}
        <circle cx="30" cy="30" r="10" fill={c.accent} />
        <circle cx="30" cy="30" r="7" fill={c.primary} opacity="0.8" />
        <circle cx="30" cy="30" r="4" fill={c.secondary} opacity="0.6" />
      </svg>
    </div>
  );
}

// Text-only version for tight spaces
export function TextLogo({ 
  className = '',
  variant = 'default'
}: { 
  className?: string;
  variant?: 'default' | 'light' | 'dark';
}) {
  const colorClass = variant === 'light' ? 'text-white' : variant === 'dark' ? 'text-foreground' : 'text-primary';
  
  return (
    <div className={`tracking-tight ${className}`}>
      <span className={colorClass}>BtGtR</span>
    </div>
  );
}

// Simple wordmark for headers
export function LogoWordmark({ 
  size = 'md',
  variant = 'default',
  className = '' 
}: {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'light' | 'dark';
  className?: string;
}) {
  const sizeClasses = {
    sm: 'h-6',
    md: 'h-8',
    lg: 'h-12'
  };

  const colors = {
    default: {
      text: '#2d3319',
      primary: '#2d5016'
    },
    light: {
      text: '#ffffff',
      primary: '#5a7139'
    },
    dark: {
      text: '#faf7f2',
      primary: '#2d5016'
    }
  };

  const c = colors[variant];

  return (
    <div className={`flex items-center ${className}`}>
      <svg
        viewBox="0 0 200 40"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x="0"
          y="28"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="20"
          fontWeight="700"
          letterSpacing="2"
          fill={c.primary}
        >
          BETTER TO GIVE THAN RECEIVE
        </text>
      </svg>
    </div>
  );
}
